// ============================================
// PRODUCT EDIT DIALOG COMPONENT
// Dialog for creating/editing products
// Extracted from POSPageV2.jsx
// ============================================

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Edit, Check } from 'lucide-react';

const ProductEditDialog = ({
    open,
    onOpenChange,
    productData,
    setProductData,
    isNew,
    categories,
    accompaniments,
    supplements,
    onSave,
}) => {
    const handleClose = () => {
        onOpenChange(false);
    };

    const updateProduct = (key, value) => {
        setProductData(prev => ({
            ...prev,
            product: { ...prev.product, [key]: value }
        }));
    };

    const toggleAccompaniment = (accId) => {
        setProductData(prev => {
            const current = prev.product.allowed_accompaniments || [];
            const newList = current.includes(accId)
                ? current.filter(id => id !== accId)
                : [...current, accId];
            return { ...prev, product: { ...prev.product, allowed_accompaniments: newList } };
        });
    };

    const toggleSupplement = (supId) => {
        setProductData(prev => {
            const current = prev.product.allowed_supplements || [];
            const newList = current.includes(supId)
                ? current.filter(id => id !== supId)
                : [...current, supId];
            return { ...prev, product: { ...prev.product, allowed_supplements: newList } };
        });
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Edit className="w-5 h-5 text-blue-500" />
                        {isNew ? 'Nouveau produit' : 'Modifier le produit'}
                    </DialogTitle>
                </DialogHeader>
                {productData && (
                    <div className="py-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Nom *</Label>
                                <Input
                                    value={productData.name || ''}
                                    onChange={(e) => updateProduct('name', e.target.value)}
                                    placeholder="Nom du produit"
                                />
                            </div>
                            <div>
                                <Label>Prix (FCFA) *</Label>
                                <Input
                                    type="number"
                                    value={productData.price || ''}
                                    onChange={(e) => updateProduct('price', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Catégorie</Label>
                                <Select
                                    value={productData.category || ''}
                                    onValueChange={(v) => updateProduct('category', v)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Choisir" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Destination</Label>
                                <Select
                                    value={productData.destination || ''}
                                    onValueChange={(v) => updateProduct('destination', v)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Où envoyer ?" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cuisine">Cuisine</SelectItem>
                                        <SelectItem value="bar">Bar</SelectItem>
                                        <SelectItem value="both">Les deux</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label>Emoji / Icône</Label>
                            <Input
                                value={productData.icon || ''}
                                onChange={(e) => updateProduct('icon', e.target.value)}
                                placeholder="Emoji"
                            />
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div>
                                <p className="font-medium">Produit actif</p>
                                <p className="text-xs text-muted-foreground">Visible dans la caisse</p>
                            </div>
                            <Switch
                                checked={productData.is_available !== false}
                                onCheckedChange={(v) => updateProduct('is_available', v)}
                            />
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                            <div>
                                <p className="font-medium">Accompagnement requis</p>
                                <p className="text-xs text-muted-foreground">Le serveur doit choisir un accompagnement</p>
                            </div>
                            <Switch
                                checked={productData.has_accompaniments || false}
                                onCheckedChange={(v) => updateProduct('has_accompaniments', v)}
                            />
                        </div>
                        
                        {productData.has_accompaniments && (
                            <div className="p-3 border rounded-lg">
                                <Label className="mb-2 block">Accompagnements autorisés</Label>
                                <div className="flex flex-wrap gap-2">
                                    {accompaniments.map(acc => (
                                        <Badge
                                            key={acc.id}
                                            variant={productData.allowed_accompaniments?.includes(acc.id) ? "default" : "outline"}
                                            className="cursor-pointer"
                                            onClick={() => toggleAccompaniment(acc.id)}
                                        >
                                            {acc.icon} {acc.name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        <div className="p-3 border rounded-lg">
                            <Label className="mb-2 block">Suppléments autorisés</Label>
                            <div className="flex flex-wrap gap-2">
                                {supplements.map(sup => (
                                    <Badge
                                        key={sup.id}
                                        variant={productData.allowed_supplements?.includes(sup.id) ? "default" : "outline"}
                                        className="cursor-pointer"
                                        onClick={() => toggleSupplement(sup.id)}
                                    >
                                        {sup.name}
                                    </Badge>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">Laissez vide pour autoriser tous les suppléments</p>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose}>
                        Annuler
                    </Button>
                    <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700">
                        <Check className="w-4 h-4 mr-2" />
                        {isNew ? 'Créer' : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ProductEditDialog;
